import { useState } from 'react';
import { createPortal } from 'react-dom';
import { useApp } from '../../state/AppContext';
import Icon from '../../lib/Icon';
import { badge, zeb } from '../../lib/format';
import { card, cardPad } from '../../lib/ui';

interface Report {
  ref: string;
  category: string;
  description: string;
  anon: boolean;
  contact: string;
  evidence: string;
  submitted: string;
  status: string;
}

// Reports submitted through the public "Report Details" whistleblower form.
const REPORTS: Report[] = [
  { ref: 'WB-2026-0011', category: 'Bid Rigging', description: 'Two invited insurers on RFQ-2026-004 appear to have coordinated their pricing — near-identical premium tables submitted minutes apart.', anon: true, contact: '—', evidence: '2 files', submitted: '2026-07-04', status: 'New' },
  { ref: 'WB-2026-0009', category: 'Conflict of Interest', description: 'A procurement officer is related to a director of a shortlisted supplier and did not declare it before review.', anon: true, contact: '—', evidence: '1 file', submitted: '2026-06-29', status: 'Under Review' },
  { ref: 'WB-2026-0007', category: 'Bribery / Kickback', description: 'A shortlisted bidder reportedly offered gifts to an evaluator during the Class 1 tender.', anon: false, contact: 'j.tan@acme-corp.com', evidence: '3 files', submitted: '2026-06-21', status: 'Under Review' },
  { ref: 'WB-2026-0004', category: 'Fraudulent Invoice', description: 'Invoice INV-2026-0063 exceeds the PO value; suspected inflated line items on the fleet policy.', anon: true, contact: '—', evidence: 'None', submitted: '2026-06-12', status: 'Resolved' },
  { ref: 'WB-2026-0002', category: 'Related Party Transaction', description: 'Recurring awards to a supplier that shares ownership with a group affiliate, without RPT disclosure.', anon: false, contact: 'audit.tip@acme-corp.com', evidence: '1 file', submitted: '2026-06-03', status: 'Resolved' },
];

const KPIS = [
  { label: 'New', value: '1', color: 'var(--color-error)' },
  { label: 'Under Review', value: '2', color: 'var(--color-warning)' },
  { label: 'Resolved (FY)', value: '2', color: 'var(--color-success)' },
  { label: 'Anonymous', value: '3 of 5', color: 'var(--color-fg-1)' },
];

// Thai lookups for enum-like values (plain data — L() is applied at render).
const CAT_TH: Record<string, string> = {
  'Bid Rigging': 'ฮั้วประมูล',
  'Conflict of Interest': 'ผลประโยชน์ทับซ้อน',
  'Bribery / Kickback': 'ติดสินบน / เงินใต้โต๊ะ',
  'Fraudulent Invoice': 'ใบแจ้งหนี้ปลอม',
  'Related Party Transaction': 'รายการกับบุคคลที่เกี่ยวโยงกัน',
};
const STAT_TH: Record<string, string> = { 'New': 'ใหม่', 'Under Review': 'กำลังตรวจสอบ', 'Resolved': 'แก้ไขแล้ว' };
const KPI_TH: Record<string, string> = { 'New': 'ใหม่', 'Under Review': 'กำลังตรวจสอบ', 'Resolved (FY)': 'แก้ไขแล้ว (ปีงบ)', 'Anonymous': 'ไม่ระบุตัวตน' };
const evTh = (v: string) => (v === 'None' ? 'ไม่มี' : v.replace('files', 'ไฟล์').replace('file', 'ไฟล์'));

function priorityOf(status: string) {
  if (status === 'New') return { bg: 'var(--color-error-bg)', fg: 'var(--color-error)' };
  if (status === 'Under Review') return { bg: 'var(--color-warning-bg)', fg: 'var(--color-warning)' };
  return { bg: 'var(--color-success-bg)', fg: 'var(--color-success)' };
}

export default function Reports() {
  const { L } = useApp();
  const [selected, setSelected] = useState<Report | null>(null);

  return (
    <>
      <div className="dash-stats" style={{ display: 'grid', gridTemplateColumns: 'repeat(4,1fr)', gap: 16, marginBottom: 16 }}>
        {KPIS.map((k) => (
          <div key={k.label} style={cardPad('16px 18px')}>
            <div style={{ fontSize: 11, fontWeight: 700, textTransform: 'uppercase', color: 'var(--color-fg-3)', marginBottom: 6 }}>{L(k.label, KPI_TH[k.label] ?? k.label)}</div>
            <div style={{ fontSize: 22, fontWeight: 700, fontFamily: 'var(--font-mono)', color: k.color }}>{L(k.value, k.value === '3 of 5' ? '3 จาก 5' : k.value)}</div>
          </div>
        ))}
      </div>

      <div style={cardPad(0, 'auto')}>
        <div style={{ padding: '14px 18px', fontWeight: 700, borderBottom: '1px solid var(--color-border)' }}>{L('All Report Submissions', 'รายงานที่ส่งเข้ามาทั้งหมด')}</div>
        <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13, minWidth: 880 }}>
          <thead>
            <tr style={{ background: 'var(--color-mist)', borderBottom: '1px solid var(--color-border)' }}>
              {[L('Report Ref', 'เลขที่รายงาน'), L('Category', 'หมวดหมู่'), L('Summary', 'สรุป'), L('Type', 'ประเภท'), L('Evidence', 'หลักฐาน'), L('Submitted', 'ส่งเมื่อ'), L('Status', 'สถานะ'), ''].map((h, i) => (
                <th key={i} style={{ textAlign: i === 7 ? 'right' : 'left', padding: '11px 16px', fontSize: 10, fontWeight: 700, textTransform: 'uppercase', color: 'var(--color-fg-3)' }}>{h}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {REPORTS.map((r, i) => {
              const b = badge(r.status === 'Resolved' ? 'Verified' : r.status === 'New' ? 'Pending' : 'Under Review');
              return (
                <tr key={r.ref} style={{ borderBottom: '1px solid var(--color-border)', background: zeb(i) }}>
                  <td style={{ padding: '12px 16px', fontFamily: 'var(--font-mono)', fontWeight: 600 }}>{r.ref}</td>
                  <td style={{ padding: '12px 16px' }}><span style={{ fontSize: 11, fontWeight: 600, padding: '3px 9px', borderRadius: 999, background: 'var(--color-ink-50)', color: 'var(--color-fg-2)' }}>{L(r.category, CAT_TH[r.category] ?? r.category)}</span></td>
                  <td style={{ padding: '12px 16px', color: 'var(--color-fg-2)', maxWidth: 300, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{r.description}</td>
                  <td style={{ padding: '12px 16px', color: 'var(--color-fg-2)' }}>{r.anon ? L('Anonymous', 'ไม่ระบุตัวตน') : L('Named', 'ระบุตัวตน')}</td>
                  <td style={{ padding: '12px 16px', color: 'var(--color-fg-2)' }}>{L(r.evidence, evTh(r.evidence))}</td>
                  <td style={{ padding: '12px 16px', fontFamily: 'var(--font-mono)', color: 'var(--color-fg-2)' }}>{r.submitted}</td>
                  <td style={{ padding: '12px 16px' }}><span style={{ fontSize: 11, fontWeight: 600, padding: '3px 10px', borderRadius: 999, background: b.bg, color: b.fg }}>{L(r.status, STAT_TH[r.status] ?? r.status)}</span></td>
                  <td style={{ padding: '12px 16px', textAlign: 'right' }}>
                    <button onClick={() => setSelected(r)} className="hv-primary" style={{ fontSize: 12, fontWeight: 600, padding: '6px 12px', borderRadius: 'var(--radius-sm)', background: 'var(--color-primary)', color: '#fff' }}>{L('Review', 'ตรวจสอบ')}</button>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {selected && <ReportDetail report={selected} onClose={() => setSelected(null)} />}
    </>
  );
}

function ReportDetail({ report, onClose }: { report: Report; onClose: () => void }) {
  const { L } = useApp();
  const p = priorityOf(report.status);
  const rows: [string, string, boolean][] = [
    [L('Report Ref', 'เลขที่รายงาน'), report.ref, true],
    [L('Category', 'หมวดหมู่'), L(report.category, CAT_TH[report.category] ?? report.category), false],
    [L('Submission Type', 'ประเภทการส่ง'), report.anon ? L('Anonymous', 'ไม่ระบุตัวตน') : L('Named', 'ระบุตัวตน'), false],
    [L('Contact', 'ผู้ติดต่อ'), report.contact, false],
    [L('Evidence', 'หลักฐาน'), L(report.evidence, evTh(report.evidence)), false],
    [L('Submitted', 'ส่งเมื่อ'), report.submitted, false],
  ];
  return createPortal((
    <div onClick={onClose} style={{ position: 'fixed', inset: 0, background: 'rgba(14,23,38,.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 60, animation: 'aibid-scrim .2s', padding: 20, overflow: 'auto' }}>
      <div onClick={(e) => e.stopPropagation()} className="aibid-fade" style={{ ...card, width: 620, maxWidth: '96vw', overflow: 'hidden' }}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '16px 20px', borderBottom: '1px solid var(--color-border)' }}>
          <div style={{ fontWeight: 700, fontSize: 16 }}>{L('Report Details', 'รายละเอียดรายงาน')}</div>
          <span style={{ fontSize: 11, fontWeight: 600, padding: '3px 10px', borderRadius: 999, background: p.bg, color: p.fg }}>{L(report.status, STAT_TH[report.status] ?? report.status)}</span>
        </div>
        <div style={{ padding: '20px' }}>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '10px 24px', marginBottom: 18 }}>
            {rows.map(([k, v, mono]) => (
              <div key={k} style={{ display: 'flex', justifyContent: 'space-between', gap: 12, padding: '6px 0', borderBottom: '1px solid var(--color-border)', fontSize: 13 }}>
                <span style={{ color: 'var(--color-fg-3)' }}>{k}</span>
                <span style={{ fontWeight: 600, textAlign: 'right', fontFamily: mono ? 'var(--font-mono)' : 'var(--font-sans)' }}>{v}</span>
              </div>
            ))}
          </div>
          <div style={{ fontSize: 11, fontWeight: 700, textTransform: 'uppercase', color: 'var(--color-fg-3)', marginBottom: 6 }}>{L('Description', 'รายละเอียด')}</div>
          <div style={{ fontSize: 13, color: 'var(--color-fg-1)', lineHeight: 1.55, padding: '12px 14px', background: 'var(--color-mist)', borderRadius: 'var(--radius-sm)' }}>{report.description}</div>
        </div>
        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8, padding: '14px 20px', background: 'var(--color-mist)', borderTop: '1px solid var(--color-border)', flexWrap: 'wrap' }}>
          <button onClick={onClose} style={{ padding: '8px 14px', border: '1px solid var(--color-border-strong)', borderRadius: 'var(--radius-sm)', fontWeight: 600, fontSize: 13, background: 'var(--color-white)' }}>{L('Close', 'ปิด')}</button>
          <button onClick={onClose} className="hv-primary" style={{ padding: '8px 14px', background: 'var(--color-primary)', color: '#fff', borderRadius: 'var(--radius-sm)', fontWeight: 600, fontSize: 13 }}>{L('Assign to Audit Committee', 'มอบหมายให้คณะกรรมการตรวจสอบ')}</button>
        </div>
      </div>
    </div>
  ), document.body);
}
